import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * Body of `POST /school/report-cards/generate` (#107): every student of a group for one period.
 */
export const generateReportCardsInputSchema = z.object({
  groupId: uuid,
  periodId: uuid,
});

/**
 * Body of `POST /school/report-cards/generate`.
 */
export type GenerateReportCardsInput = z.infer<typeof generateReportCardsInputSchema>;

/**
 * Query of `GET /students/{id}/report-cards`: the school year to list (the current one by
 * default).
 */
export const reportCardListQuerySchema = z.object({ schoolYearId: uuid.optional() });

/**
 * Query of `GET /students/{id}/report-cards`.
 */
export type ReportCardListQuery = z.infer<typeof reportCardListQuerySchema>;

/**
 * Where a batch is: waiting for its worker, rendering, finished, or stopped by an error that was
 * not one student's.
 */
export type ReportCardBatchStatus = 'queued' | 'running' | 'done' | 'failed';

/**
 * A student whose PDF the batch could not render, with the reason.
 */
export type ReportCardBatchError = { studentId: string; studentName: string; message: string };

/**
 * A batch as `POST /school/report-cards/generate` and `GET /school/report-cards/batches/{id}`
 * answer it: the counts move while the worker runs.
 */
export type ReportCardBatchView = {
  id: string;
  groupId: string;
  groupName: string;
  periodId: string;
  periodName: string;
  status: ReportCardBatchStatus;
  /**
   * Students of the group the batch renders.
   */
  total: number;
  generated: number;
  failed: number;
  errors: ReportCardBatchError[];
  error: string | null;
  requestedByUserId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
};

/**
 * One period of a student's year as the report-card list shows it: what a boletín would
 * carry and whether a file exists already.
 */
export type ReportCardListItem = {
  periodId: string;
  periodName: string;
  position: number;
  startsOn: string;
  endsOn: string;
  /**
   * Subjects with a published grade for the student in the period. Zero means no boletín yet.
   */
  publishedSubjects: number;
  /**
   * When the stored PDF was rendered, or null when none has been generated.
   */
  generatedAt: string | null;
};

/**
 * Answer of `GET /students/{id}/report-cards`.
 */
export type ReportCardList = {
  studentId: string;
  schoolYear: { id: string; name: string } | null;
  items: ReportCardListItem[];
};

/**
 * Answer of `GET /students/{id}/report-cards/{periodId}`: a short-lived link to the PDF.
 */
export type ReportCardDownload = {
  url: string;
  expiresAt: string;
  filename: string;
  sizeBytes: number;
  generatedAt: string;
};
